import { makeEnvelope } from '../envelope/envelope';
import { zeroHlc } from '../hlc/hlc';
import { repeatDraftId, templateDraftId } from '../ids/ids';
import { spansOverlap, dayBounds } from '../geometry/span';
import { BlockStatus, DraftSource, RecordType } from '../model/enums';
import type { Settings } from '../model/settings';
import { isLive, type LiveTimeBlock, type TemplateRecord, type TimeBlockRecord } from '../model/types';
import { addDays, daysBetween } from '../time/date';

export interface GenerateDraftsInput {
  /** Personal date to fill. */
  readonly date: string;
  readonly templates: readonly TemplateRecord[];
  /** Blocks of the previous days. Only confirmed, live blocks of the last `draftRepeatDays` days count. */
  readonly recentConfirmedBlocks: readonly TimeBlockRecord[];
  readonly settings: Settings;
  /**
   * Every stored block record of `date`, tombstones included: live blocks block overlapping
   * drafts, and any existing id (including a deleted one) is never generated again.
   */
  readonly existingBlocksForDate: readonly TimeBlockRecord[];
  readonly deviceId: string;
  readonly tz: string;
  /** When given, drafts of projects outside this set (inactive or deleted) are skipped. */
  readonly activeProjectIds?: ReadonlySet<string>;
}

interface Candidate {
  readonly id: string;
  readonly projectId: string;
  readonly startMin: number;
  readonly endMin: number;
  readonly source: DraftSource;
}

function byStartThenId(a: Candidate, b: Candidate): number {
  return a.startMin - b.startMin || a.endMin - b.endMin || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

function templateCandidates(input: GenerateDraftsInput): Candidate[] {
  const bounds = dayBounds(input.settings.dayStartMin);
  return input.templates
    .filter(isLive)
    .filter((template) => template.data.startMin >= bounds.startMin && template.data.endMin <= bounds.endMin)
    .map((template) => ({
      id: templateDraftId(input.date, template.id),
      projectId: template.data.projectId,
      startMin: template.data.startMin,
      endMin: template.data.endMin,
      source: DraftSource.Template,
    }))
    .sort(byStartThenId);
}

/** (project, start, end) seen on at least `draftRepeatMinHits` of the last `draftRepeatDays` days. */
function repeatCandidates(input: GenerateDraftsInput): Candidate[] {
  const { date, settings } = input;
  const daysByKey = new Map<string, { block: LiveTimeBlock; dates: Set<string> }>();
  for (const block of input.recentConfirmedBlocks) {
    if (!isLive(block) || block.data.status !== BlockStatus.Confirmed) continue;
    const age = daysBetween(block.data.personalDate, date);
    if (age < 1 || age > settings.draftRepeatDays) continue;
    const key = `${block.data.projectId}:${block.data.startMin}-${block.data.endMin}`;
    const entry = daysByKey.get(key) ?? { block, dates: new Set<string>() };
    entry.dates.add(block.data.personalDate);
    daysByKey.set(key, entry);
  }
  const bounds = dayBounds(settings.dayStartMin);
  return [...daysByKey.values()]
    .filter(({ dates }) => dates.size >= settings.draftRepeatMinHits)
    .filter(({ block }) => block.data.startMin >= bounds.startMin && block.data.endMin <= bounds.endMin)
    .map(({ block }) => ({
      id: repeatDraftId(date, block.data.projectId, block.data.startMin, block.data.endMin),
      projectId: block.data.projectId,
      startMin: block.data.startMin,
      endMin: block.data.endMin,
      source: DraftSource.Repeat,
    }))
    .sort(byStartThenId);
}

/**
 * Draft blocks for `date` (requirements §3.3): template drafts first, then repeat drafts.
 * A draft is skipped when its project is inactive, its id already exists (even deleted: the
 * user cleared it), or it overlaps a live block or an earlier draft. Drafts carry the zero-time
 * HLC so any user edit, on any device, is newer than the generated version.
 */
export function generateDrafts(input: GenerateDraftsInput): TimeBlockRecord[] {
  const existingIds = new Set(input.existingBlocksForDate.map((block) => block.id));
  const occupied: { startMin: number; endMin: number }[] = input.existingBlocksForDate
    .filter(isLive)
    .map((block) => ({ startMin: block.data.startMin, endMin: block.data.endMin }));
  const hlc = zeroHlc(input.deviceId);
  const drafts: TimeBlockRecord[] = [];

  for (const candidate of [...templateCandidates(input), ...repeatCandidates(input)]) {
    if (existingIds.has(candidate.id)) continue;
    if (input.activeProjectIds !== undefined && !input.activeProjectIds.has(candidate.projectId)) {
      continue;
    }
    if (occupied.some((span) => spansOverlap(span, candidate))) continue;
    existingIds.add(candidate.id);
    occupied.push({ startMin: candidate.startMin, endMin: candidate.endMin });
    drafts.push(
      makeEnvelope({
        id: candidate.id,
        type: RecordType.TimeBlock,
        hlc,
        deviceId: input.deviceId,
        data: {
          personalDate: input.date,
          startMin: candidate.startMin,
          endMin: candidate.endMin,
          tz: input.tz,
          projectId: candidate.projectId,
          status: BlockStatus.Draft,
          note: null,
          draftSource: candidate.source,
        },
      }),
    );
  }
  return drafts;
}

/** The personal dates whose confirmed blocks feed the repeat rule for `date` (oldest first). */
export function repeatLookbackDates(date: string, settings: Pick<Settings, 'draftRepeatDays'>): string[] {
  return Array.from({ length: settings.draftRepeatDays }, (_, index) =>
    addDays(date, index - settings.draftRepeatDays),
  );
}
